import React, { useEffect, useCallback } from "react"
import { useIntl } from "gatsby-plugin-react-intl"
import styled from "styled-components"
import PropTypes from "prop-types"
import { DialogOverlay, DialogContent } from "@reach/dialog"
import "@reach/dialog/styles.css"

import { ScreenReaderText } from "./Accessibility"

import CloseSVG from "../images/icons/lightbox-close.inline.svg"

const Header = styled.div`
  display: flex;
`

const CloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  margin: 0 0 20px auto;
  padding: 10px;
  color: var(--color-spaceblue);
  transition: color var(--transition-time);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover,
  &:focus {
    color: var(--color-neutral-dark);
  }
`

const Lightbox = ({
  ariaLabel,
  children,
  close,
  keepOpenOnOverlayClick,
  keepOpenOnEscape,
  show,
  ...rest
}) => {
  const intl = useIntl()
  // When keepOpenOnOverlayClick is true, the ESC key no longer works.
  // We're restoring that functionality here.
  const escFunction = useCallback(
    (event) => {
      if (keepOpenOnOverlayClick && !keepOpenOnEscape && event.keyCode === 27) {
        close()
      }
    },
    [close, keepOpenOnOverlayClick, keepOpenOnEscape]
  )

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false)
    return () => {
      document.removeEventListener("keydown", escFunction, false)
    }
  }, [escFunction])

  return (
    <DialogOverlay
      isOpen={show}
      onDismiss={keepOpenOnOverlayClick ? undefined : close}
      {...rest}
    >
      <DialogContent aria-label={ariaLabel} data-testid="lightbox">
        <Header>
          <CloseButton
            onClick={close}
            aria-label={intl.formatMessage({ id: "close" })}
          >
            <CloseSVG aria-hidden="true" />
            <ScreenReaderText>
              {intl.formatMessage({ id: "close" })}
            </ScreenReaderText>
          </CloseButton>
        </Header>
        {children}
      </DialogContent>
    </DialogOverlay>
  )
}

Lightbox.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  close: PropTypes.func.isRequired,
  keepOpenOnEscapeKey: PropTypes.bool, // this only applies if keepOpenOnOverlayClick === true
  keepOpenOnOverlayClick: PropTypes.bool,
  show: PropTypes.bool.isRequired,
}

export default Lightbox
